import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useSelector } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
} from 'chart.js'
import {
  calculateCriticalRiskRatio,
  calculateShortTermCriticalRiskRatio,
  calculateShortTermRiskRatio,
  countRisksByCriticality,
  getPlanbyId,
  getSecurityControlsByPlan,
} from '../../redux/plans/planActions'
import { analysisColorPalette } from '../../utils/colors'
import PieChart, { hasChartData } from '../../components/common/PieChart'
import MatrixComponent from '../../components/common/MatrixTable'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import {
  AnalysisChartCard,
  AnalysisKpiCard,
  AnalysisPageLoader,
  AnalysisSection,
  pctTone,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import '../../components/common/maturity-analysis.css'
import { formatDecimal } from '../../utils/formatNumbers'

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

const COLORS = analysisColorPalette

const PTRIndicatorPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const delayedRisks = 1

  const dispatch: AppDispatch = useAppDispatch()
  const { PlanId } = useParams<{ PlanId: string }>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!PlanId) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await dispatch(getPlanbyId(PlanId))
        await dispatch(calculateCriticalRiskRatio({ idPlan: PlanId }))
        await dispatch(countRisksByCriticality({ idPlan: PlanId }))
        await dispatch(calculateShortTermCriticalRiskRatio({ idPlan: PlanId }))
        await dispatch(getSecurityControlsByPlan({ idPlan: PlanId }))
        await dispatch(calculateShortTermRiskRatio({ idPlan: PlanId }))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [PlanId, dispatch])

  const CriticalRiskRatio = useSelector(
    (state: RootState) => state.plan.calculateCriticalRisk?.ratio
  )

  const RiskByCritical = useSelector(
    (state: RootState) => state.plan.countRisksByCriticality
  )

  const ShortTermRiskRatio = useSelector(
    (state: RootState) => state.plan.calculateShortTermRisk
  )
  const ShortTermCriticalRiskRatio = useSelector(
    (state: RootState) => state.plan.calculateShortTermCriticalRisk
  )
  const securityControlsByRisk = useSelector(
    (state: RootState) =>
      state.plan.getSecurityControlsByPlan?.securityControlsByRisk
  )

  const risksCriticalityData = {
    labels: [
      t('charts.risksByCriticality.labels.low'),
      t('charts.risksByCriticality.labels.medium'),
      t('charts.risksByCriticality.labels.high'),
      t('charts.risksByCriticality.labels.critical'),
    ],
    datasets: [
      {
        data: [
          RiskByCritical?.counts.Low ?? 0,
          RiskByCritical?.counts.Medium ?? 0,
          RiskByCritical?.counts.High ?? 0,
          RiskByCritical?.counts.Critical ?? 0,
        ],
        backgroundColor: COLORS.slice(0, 4),
        hoverBackgroundColor: COLORS.slice(0, 4),
        hoverOffset: 4,
      },
    ],
  }

  const risksPriorityData = {
    labels: [
      t('charts.risksByPriority.labels.short'),
      t('charts.risksByPriority.labels.shortCritical'),
    ],
    datasets: [
      {
        data: [
          ShortTermRiskRatio?.ratio ?? 0,
          ShortTermCriticalRiskRatio?.ratio ?? 0,
        ],
        backgroundColor: COLORS.slice(4, 7),
        hoverBackgroundColor: COLORS.slice(4, 7),
        hoverOffset: 4,
      },
    ],
  }
  const plan = useSelector((state: RootState) => state.plan.plan)

  const riskTypeHeader = t('tables.securityControlsByRisk.headers.riskType')
  const controlsHeader = t('tables.securityControlsByRisk.headers.controls')
  const emptyControls = t('tables.securityControlsByRisk.empty')

  const formattedSecurityControls = Object.entries(
    securityControlsByRisk || {}
  ).map(([riskType, controls]) => {
    const list = Array.isArray(controls) ? controls.filter(Boolean) : []
    return {
      riskType,
      controls: list,
    }
  })

  const headers = [riskTypeHeader, controlsHeader]

  const data = formattedSecurityControls.map(({ riskType, controls }) => ({
    [riskTypeHeader]: riskType,
    [controlsHeader]:
      controls.length > 0 ? (
        <div className="ma-analysis-chips">
          {controls.map((control) => (
            <span key={`${riskType}-${control}`} className="ma-analysis-chip">
              {control}
            </span>
          ))}
        </div>
      ) : (
        <span className="ma-analysis-chip is-empty">{emptyControls}</span>
      ),
  }))
  const pdf = useAnalysisPdfExport()

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const criticalPct = Math.min(
    100,
    Math.max(0, Number(CriticalRiskRatio) * 100 || 0)
  )
  const delayedPct = Math.min(100, Math.max(0, delayedRisks * 100 || 0))
  const stampTitle = t('analysis.rtkAnalysis.computedOn', { date: computedOn })
  const backPath = PlanId ? `/plan-detail/${PlanId}` : '/plans'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.rtkAnalysis.eyebrow')}
        title={t('analysis.rtkAnalysis.pageTitle')}
        subtitle={t('analysis.rtkAnalysis.subtitle', {
          name: plan?.PlanName || '—',
          date: computedOn,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.rtkAnalysis.backToPlan')}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() => pdf.exportPdf(plan?.PlanName || 'plan-analysis')}
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={2} chartCount={2} />
      ) : (
        <>
      <div className="ma-kpi-grid">
        <AnalysisKpiCard
          label={t('charts.criticalRisks.title')}
          value={formatDecimal(criticalPct)}
          suffix="%"
          tone={pctTone(criticalPct)}
          progress={criticalPct}
          stampTitle={stampTitle}
          description={t('analysis.rtkAnalysis.criticalRisksDesc')}
        />
        <AnalysisKpiCard
          label={t('charts.delayedRisks.title')}
          value={formatDecimal(delayedPct)}
          suffix="%"
          tone={pctTone(delayedPct)}
          progress={delayedPct}
          stampTitle={stampTitle}
          description={t('analysis.rtkAnalysis.delayedRisksDesc')}
        />
      </div>

      <AnalysisSection label={t('analysis.rtkAnalysis.sectionRisks')}>
        <AnalysisChartCard
          title={t('charts.risksByCriticality.title')}
          hasData={hasChartData(risksCriticalityData)}
        >
          <PieChart
            embedded
            title={t('charts.risksByCriticality.title')}
            chartTitle={t('charts.risksByCriticality.title')}
            data={risksCriticalityData}
            itemRow={3}
          />
        </AnalysisChartCard>
        <AnalysisChartCard
          title={t('charts.risksByPriority.title')}
          hasData={hasChartData(risksPriorityData)}
        >
          <PieChart
            embedded
            title={t('charts.risksByPriority.title')}
            chartTitle={t('charts.risksByPriority.title')}
            data={risksPriorityData}
            itemRow={3}
          />
        </AnalysisChartCard>
      </AnalysisSection>

      <p className="ma-section-label">
        {t('analysis.rtkAnalysis.sectionControls')}
      </p>
      <div className="ma-card">
        <div className="ma-card-head">
          <h3>{t('tables.securityControlsByRisk.matrixTitle')}</h3>
          <span className="ma-card-hint">
            {t('analysis.rtkAnalysis.controlsTableHint', {
              count: data.length,
              defaultValue: `${data.length} risk types`,
            })}
          </span>
        </div>
        <div className="ma-card-body ma-card-body--table">
          <MatrixComponent
            variant="analysis"
            maxHeight="min(520px, 60vh)"
            title={t('tables.securityControlsByRisk.matrixTitle')}
            data={data}
            headers={headers}
          />
        </div>
      </div>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.rtkAnalysis.eyebrow')}
          title={plan?.PlanName || '—'}
          subtitle={t('analysis.rtkAnalysis.subtitle', {
            name: plan?.PlanName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.rtkAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.criticalRisks.title'),
              value: `${formatDecimal(criticalPct)} %`,
              desc: t('analysis.rtkAnalysis.criticalRisksDesc'),
              tone: pctTone(criticalPct),
              progress: criticalPct,
            },
            {
              label: t('charts.delayedRisks.title'),
              value: `${formatDecimal(delayedPct)} %`,
              desc: t('analysis.rtkAnalysis.delayedRisksDesc'),
              tone: pctTone(delayedPct),
              progress: delayedPct,
            },
          ]}
          sections={[
            {
              label: t('analysis.rtkAnalysis.sectionRisks'),
              charts: [
                {
                  key: 'criticality',
                  title: t('charts.risksByCriticality.title'),
                  kind: 'pie',
                  data: risksCriticalityData,
                },
                {
                  key: 'priority',
                  title: t('charts.risksByPriority.title'),
                  kind: 'pie',
                  data: risksPriorityData,
                },
              ],
            },
          ]}
          tables={[
            {
              label: t('analysis.rtkAnalysis.sectionControls'),
              title: t('tables.securityControlsByRisk.matrixTitle'),
              hint: t('analysis.rtkAnalysis.controlsTableHint', {
                count: formattedSecurityControls.length,
              }),
              headers,
              rows: formattedSecurityControls.map(({ riskType, controls }) => [
                riskType,
                controls.length ? controls.join(', ') : emptyControls,
              ]),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default PTRIndicatorPage
